"use client";

import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Paper,
  Stack,
  Grid,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useThoughtCoCreate } from "@/contexts/ThoughtCoCreateContext";
import { apiRemote } from "@/api";
import { useAlert } from "@/contexts/AlertContext";
import { useUser } from "@/contexts/UserContext";
import { StyledLink } from "@/components/StyledLink";

const STATUS_DRAFT = process.env.STATUS_DRAFT;
const STATUS_SUBMITTED = process.env.STATUS_SUBMITTED;
const STATUS_NOT_STARTED = process.env.STATUS_NOT_STARTED;
const STATUS_IN_PROGRESS = process.env.STATUS_IN_PROGRESS;
const STATUS_ENDED = process.env.STATUS_ENDED;

export default function Deliverable3() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { userRoundParts, loading, loadUserRound1 } = useThoughtCoCreate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const alert = useAlert();
  const { user } = useUser();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [aiProposal, setAiProposal] = useState<string>("");
  const localStorageKey = `draft_deliverable3_${user?.user_login}_${id}`;
  const [isCleared, setIsCleared] = useState(false);

  const [formData, setFormData] = useState(() => {
    const savedData = localStorage.getItem(localStorageKey);
    return savedData ? JSON.parse(savedData) : {
      strengths: "",
      weaknesses: "",
      feasibility: "",
      ethics: "",
    };
  });

  useEffect(() => {
    if (!isCleared) {
      localStorage.setItem(localStorageKey, JSON.stringify(formData));
    }
  }, [formData]);

  const validateForm = () => {
    let newErrors: { [key: string]: string } = {};

    if (!formData.strengths) newErrors.strengths = "Required";
    if (!formData.weaknesses) newErrors.weaknesses = "Required";
    if (!formData.feasibility) newErrors.feasibility = "Required";
    if (!formData.ethics) newErrors.ethics = "Required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const current_round = Number(id) - 1;
    const previous_round = Number(id) - 2;
    if (loading || !userRoundParts?.[current_round]) return;
    const roundData = userRoundParts[current_round];
    const previousRoundData = userRoundParts[previous_round];
    if (roundData.status == Number(STATUS_SUBMITTED)) {
      setIsSubmitted(true);
    } else {
      setIsSubmitted(false);
    }
    if (roundData.submission_data) {
      const submissionData = JSON.parse(roundData.submission_data);
      setFormData(submissionData);
    }
    if (previousRoundData.submission_data) {
      const previousSubmissionData = JSON.parse(
        previousRoundData.submission_data
      );
      setAiProposal(
        previousSubmissionData.aiFinalProposal ||
          previousSubmissionData.finalProposal
      );
    }
    const dueDate = new Date(roundData.due_date);
    const today = new Date();
    // console.log(today, dueDate)
    const dueDateOnly = new Date(
      dueDate.getFullYear(),
      dueDate.getMonth(),
      dueDate.getDate()
    );
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    if (
      previousRoundData &&
      previousRoundData.status !== Number(STATUS_DRAFT) &&
      previousRoundData.status !== Number(STATUS_SUBMITTED)
    ) {
      setStatusMessage(
        `You need to complete the previous part to work on this one.`
      );
    } else {
      // if (todayOnly < dueDateOnly && (roundData.status !== Number(STATUS_SUBMITTED))) {
      //   setStatusMessage(
      //     `Submission is not open yet. Due date: ${roundData.due_date}`
      //   );
      // } else
      if (
        todayOnly > dueDateOnly &&
        roundData.status !== Number(STATUS_SUBMITTED)
      ) {
        setStatusMessage(
          `Submission is overdue. Final deadline: ${roundData.due_date}`
        );
        setIsDeadlinePassed(true)
      } else if (
        todayOnly > dueDateOnly
      ) {
        setIsDeadlinePassed(true)
      } else {
        setStatusMessage(null); // Within the allowed time, no message needed
        setIsDeadlinePassed(false)
      }
    }
  }, [id, userRoundParts, loading]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
    setErrors({
      ...errors,
      [event.target.name]: "",
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDeadlinePassed || isSubmitting) return;

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        apiRemote
          .postStep({
            user: user?.user_login || "",
            round_part_id: Number(id),
            status: 2,
            submission_data: JSON.stringify(formData),
            step: 1,
          })
          .then((data) => {
            console.log(data);
            if (data.code == 200) {
              alert.success(data.message);
              setIsSubmitted(true);
              loadUserRound1();
              setIsCleared(true);
              localStorage.removeItem(localStorageKey);
            } else {
              alert.error(data.message);
            }
          })
          .finally(() => setIsSubmitting(false));
      } catch (error) {
        alert.error("Something was wrong!");
        console.log(error)
      }
      console.log("Form submitted successfully", formData);
    } else {
      console.log("Form has errors", errors);
    }
    // Handle submission logic here
  };

  const handleSaveDraft = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDeadlinePassed || isSubmitting) return;

    setIsSubmitting(true);

    if (validateForm()) {
      try {
        apiRemote
          .postStep({
            user: user?.user_login || "",
            round_part_id: Number(id),
            status: 1,
            submission_data: JSON.stringify(formData),
            step: 1,
          })
          .then((data) => {
            console.log(data);
            if (data.code == 200) {
              alert.success(data.message);
              setIsSubmitted(true);
              loadUserRound1();
              // setIsCleared(true);
              localStorage.removeItem(localStorageKey);
            } else {
              alert.error(data.message);
            }
          })
          .finally(() => setIsSubmitting(false));
      } catch (error) {
        alert.error("Something was wrong!");
        console.log(error)
      }
      console.log("Form submitted successfully", formData);
    } else {
      console.log("Form has errors", errors);
    }
  };

  const handleNext = () => {
    const nextId = Number(id) + 1;
    navigate(`../${nextId}`);
  };

  const handlePrevious = () => {
    const previousId = Number(id) - 1;
    navigate(`../${previousId}`);
  };

  return (
    <>
      {statusMessage ? (
        <Typography
          variant="body1"
          color={statusMessage.includes("overdue") ? "error" : "warning"}
          sx={{ mb: 2 }}
        >
          {statusMessage}
        </Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Paper
                sx={{
                  p: { xs: 2, md: 3 },
                  mb: { xs: 2, md: 3 },
                  maxHeight: { xs: "300px", md: "700px" },
                  overflow: "auto",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  AI Proposal
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "pre-wrap",
                    fontSize: { xs: "0.875rem", md: "1rem" },
                  }}
                >
                  {aiProposal || "No AI proposal available."}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={7}>
              <Paper
                sx={{
                  p: { xs: 2, md: 3 },
                  mb: { xs: 2, md: 3 },
                  "& .MuiTextField-root": {
                    mb: { xs: 2, md: 3 },
                  },
                }}
              >
                <Stack spacing={3}>
                  <TextField
                    label="Strengths"
                    name="strengths"
                    value={formData.strengths}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="Identify the strengths of the AI-built proposal..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "divider",
                        },
                      },
                    }}
                    error={!!errors.strengths} // Hiển thị viền đỏ nếu có lỗi
                    helperText={errors.strengths}
                    InputProps={{ readOnly: isDeadlinePassed }}
                  />

                  <TextField
                    label="Weaknesses / Limitations"
                    name="weaknesses"
                    value={formData.weaknesses}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="Identify the weaknesses or limitations of the AI-built proposal..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "divider",
                        },
                      },
                    }}
                    error={!!errors.weaknesses} // Hiển thị viền đỏ nếu có lỗi
                    helperText={errors.weaknesses}
                    InputProps={{ readOnly: isDeadlinePassed }}
                  />

                  <TextField
                    label="Feasibility or Resource Check"
                    name="feasibility"
                    value={formData.feasibility}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="Assess the feasibility of the proposal given your school's resources..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "divider",
                        },
                      },
                    }}
                    error={!!errors.feasibility} // Hiển thị viền đỏ nếu có lỗi
                    helperText={errors.feasibility}
                    InputProps={{ readOnly: isDeadlinePassed }}
                  />

                  <TextField
                    label="Ethical Considerations"
                    name="ethics"
                    value={formData.ethics}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="Discuss any ethical considerations or implications of the proposal..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "divider",
                        },
                      },
                    }}
                    error={!!errors.ethics} // Hiển thị viền đỏ nếu có lỗi
                    helperText={errors.ethics}
                    InputProps={{ readOnly: isDeadlinePassed }}
                  />
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            sx={{
              justifyContent: "space-between",
              mt: 3,
              width: "100%",
            }}
          >
            <Button
              variant="text"
              color="inherit"
              sx={{
                color: "text.primary",
                mb: isMobile ? 2 : 0,
                alignSelf: isMobile ? "flex-start" : "auto",
              }}
            >
              <StyledLink to={`../../dashboard`} style={{ color: "#000" }}>
                Back to Dashboard
              </StyledLink>
            </Button>
            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={isMobile ? 1 : 2}
              sx={{ width: isMobile ? "100%" : "auto", alignItems: "center" }}
            >
              {isSubmitting && <CircularProgress size={20} sx={{ ml: 1, verticalAlign: "middle" }} />}
              {/* <Button
                variant="text"
                fullWidth={isMobile}
                sx={{
                  color: "text.primary",
                }}
                onClick={handleSaveDraft}
                disabled={isSubmitted || isSubmitting}
              >
                Save as Draft
              </Button> */}
              <Button
                variant="contained"
                fullWidth={isMobile}
                sx={{
                  bgcolor: "black",
                  "&:hover": {
                    bgcolor: "grey.900",
                  },
                }}
                onClick={handleSubmit}
                disabled={isDeadlinePassed || isSubmitting}
              >
                Submit D3
              </Button>
              {isSubmitted && (
                <>
                  <Button
                    variant="contained"
                    fullWidth={isMobile}
                    sx={{
                      bgcolor: "blue",
                      "&:hover": {
                        bgcolor: "darkblue",
                      },
                    }}
                    onClick={handlePrevious}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth={isMobile}
                    sx={{
                      bgcolor: "blue",
                      "&:hover": {
                        bgcolor: "darkblue",
                      },
                    }}
                    onClick={handleNext}
                  >
                    Next
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </>
      )}
    </>
  );
}
